import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	AlertTriangle,
	Check,
	Loader2,
	RotateCcw,
	Shield,
	ThumbsDown,
	ThumbsUp,
	Trash2,
	X,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { PageHeader } from "@/components/ui/page-header";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	type AiVerdictFilter,
	asystentQualityKeys,
	type DeleteRecordInput,
	type DetailResponse,
	deleteAsystentQualityRecord,
	fetchAsystentQualityDetail,
	fetchAsystentQualityOverview,
	type OverviewGroup,
	type OverviewParams,
	overrideAsystentVerdict,
	type RiskLevel,
	type SourceTable,
	type TimeWindow,
} from "@/features/asystent/asystent-quality-api";
import { MobileStatGrid, polishCount } from "@/features/asystent/components/mobile-stats";
import { AiDescriptionRenderer } from "@/features/products/components/ai-description-renderer";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { ensureElementVisible } from "@/lib/route-guards";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_auth/asystent-jakosc")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.analityka.asystent-jakosc",
			location.pathname,
		);
	},
	component: AsystentJakoscPage,
});

const PAGE_SIZE = 25;

const RISK_LABEL: Record<RiskLevel, string> = {
	high: "Wysokie",
	medium: "Średnie",
	low: "Niskie",
};
const RISK_CLASS: Record<RiskLevel, string> = {
	high: "bg-status-warning-bg text-status-warning-text border border-status-warning-border",
	medium: "bg-status-info-bg text-status-info-text border border-status-info-border",
	low: "bg-muted text-muted-foreground border border-border",
};

function fmtDate(iso: string | null): string {
	if (!iso) return "—";
	// ISO z backendu: "YYYY-MM-DDTHH:MM:SS.ffffffZ" → skracamy do minut.
	return iso.replace("T", " ").slice(0, 16);
}

const PAGE_DESCRIPTION = "moderacja ocen farmaceutów i werdyktów AI";

const AI_VERDICT_LABEL: Record<AiVerdictFilter | "all", string> = {
	all: "Werdykt AI: wszystkie",
	match: "AI: pasuje",
	reject: "AI: nie pasuje",
	none: "AI: bez oceny",
};
const SOURCE_LABEL: Record<SourceTable | "all", string> = {
	all: "Wszystkie źródła",
	feedback: "Feedback",
	verdict: "Werdykty AI",
};
const WINDOW_OPTIONS: { value: TimeWindow | undefined; label: string }[] = [
	{ value: undefined, label: "Cały okres" },
	{ value: "24h", label: "Ostatnie 24h" },
	{ value: "7d", label: "7 dni" },
];

/** Przelacznik w arkuszu filtrow (telefon): wiersz 40 px z checkboxem zamiast chipa-pigulki. */
function SheetToggle({
	checked,
	onChange,
	children,
}: {
	checked: boolean;
	onChange: (checked: boolean) => void;
	children: React.ReactNode;
}) {
	return (
		<label className="flex min-h-10 cursor-pointer items-center gap-2.5 text-sm text-foreground">
			<input
				type="checkbox"
				className="size-4 accent-[var(--primary)]"
				checked={checked}
				onChange={(e) => onChange(e.target.checked)}
			/>
			{children}
		</label>
	);
}

function SummaryCard({ label, value, accent }: { label: string; value: number; accent?: boolean }) {
	return (
		<Card>
			<CardContent className="p-3">
				<div className="text-xs text-muted-foreground">{label}</div>
				<div
					className={`text-2xl font-semibold ${accent && value > 0 ? "text-status-warning-text" : ""}`}
				>
					{value}
				</div>
			</CardContent>
		</Card>
	);
}

/** Toggle chip filtra — semantyczny, klawiaturowalny (button). */
function Chip({
	active,
	onClick,
	children,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			aria-pressed={active}
			className={`h-7 rounded-full px-3 text-xs font-medium transition-colors ${
				active
					? "bg-primary text-primary-foreground"
					: "bg-muted text-muted-foreground hover:bg-accent"
			}`}
		>
			{children}
		</button>
	);
}

function AsystentJakoscPage() {
	const queryClient = useQueryClient();
	const [filters, setFilters] = useState<OverviewParams>({
		sort: "wave",
		page: 0,
		pageSize: PAGE_SIZE,
	});
	const [searchInput, setSearchInput] = useState("");
	const [selected, setSelected] = useState<{ query: string; kod07: string } | null>(null);
	const [toDelete, setToDelete] = useState<DeleteRecordInput | null>(null);
	const [reason, setReason] = useState("");
	const isBelowMd = useIsBelowMd();

	const overview = useQuery({
		queryKey: asystentQualityKeys.overview(filters),
		queryFn: () => fetchAsystentQualityOverview(filters),
	});

	const detail = useQuery({
		queryKey: selected
			? asystentQualityKeys.detail(selected.query, selected.kod07)
			: ["asystent-quality", "detail", "none"],
		queryFn: () => fetchAsystentQualityDetail(selected!.query, selected!.kod07),
		enabled: !!selected,
	});

	const del = useMutation({
		mutationFn: deleteAsystentQualityRecord,
		onSuccess: () => {
			toast.success("Rekord usunięty z bazy");
			setToDelete(null);
			setReason("");
			queryClient.invalidateQueries({ queryKey: asystentQualityKeys.all });
		},
		onError: (e: unknown) => {
			toast.error(e instanceof Error ? e.message : "Nie udało się usunąć rekordu");
		},
	});

	const override = useMutation({
		mutationFn: overrideAsystentVerdict,
		onSuccess: (r) => {
			toast.success(
				r.guardMatch ? "Ustawiono: pasuje" : "Ustawiono: nie pasuje (produkt wykluczony)",
			);
			queryClient.invalidateQueries({ queryKey: asystentQualityKeys.all });
		},
		onError: (e: unknown) => {
			toast.error(e instanceof Error ? e.message : "Nie udało się nadpisać werdyktu");
		},
	});

	const patch = (p: Partial<OverviewParams>) => setFilters((f) => ({ ...f, ...p, page: 0 }));
	const toggleWindow = (w: "24h" | "7d") => patch({ window: filters.window === w ? undefined : w });
	const resetFilters = () => {
		setSearchInput("");
		setFilters({ sort: "wave", page: 0, pageSize: PAGE_SIZE });
	};

	const groups = overview.data?.groups ?? [];
	const summary = overview.data?.summary;
	const total = overview.data?.total ?? 0;
	const threshold = overview.data?.downThreshold ?? 3;

	// Kontrolki filtrow budowane raz: desktopowy rzad i arkusz filtrow telefonu
	// roznia sie tylko klasa wymiarow.
	const aiVerdictSelect = (triggerClassName: string) => (
		<Select
			value={filters.aiVerdict ?? "all"}
			onValueChange={(v) => patch({ aiVerdict: v === "all" ? undefined : (v as AiVerdictFilter) })}
		>
			<SelectTrigger className={triggerClassName}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="all">Werdykt AI: wszystkie</SelectItem>
				<SelectItem value="match">AI: pasuje</SelectItem>
				<SelectItem value="reject">AI: nie pasuje</SelectItem>
				<SelectItem value="none">AI: bez oceny</SelectItem>
			</SelectContent>
		</Select>
	);
	const sourceSelect = (triggerClassName: string) => (
		<Select
			value={filters.source ?? "all"}
			onValueChange={(v) => patch({ source: v === "all" ? undefined : (v as SourceTable) })}
		>
			<SelectTrigger className={triggerClassName}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				<SelectItem value="all">Wszystkie źródła</SelectItem>
				<SelectItem value="feedback">Feedback</SelectItem>
				<SelectItem value="verdict">Werdykty AI</SelectItem>
			</SelectContent>
		</Select>
	);
	const applySearch = () => patch({ search: searchInput });

	// ── Telefon: chipy + arkusz filtrow (desktopowy rzad bez zmian) ──
	const recordToggles = (
		<div className="flex flex-col">
			<SheetToggle checked={!!filters.waveOnly} onChange={(v) => patch({ waveOnly: v })}>
				Podejrzane fale
			</SheetToggle>
			<SheetToggle checked={!!filters.negativeOnly} onChange={(v) => patch({ negativeOnly: v })}>
				Tylko negatywne
			</SheetToggle>
		</div>
	);
	const windowPicker = (
		<div className="grid grid-cols-3 gap-1 rounded-md border border-border bg-card p-0.5">
			{WINDOW_OPTIONS.map((o) => (
				<button
					key={o.label}
					type="button"
					aria-pressed={filters.window === o.value}
					onClick={() => patch({ window: o.value })}
					className={cn(
						"h-9 rounded-sm px-2 text-sm transition-colors",
						filters.window === o.value
							? "bg-primary text-primary-foreground"
							: "text-muted-foreground hover:bg-accent",
					)}
				>
					{o.label}
				</button>
			))}
		</div>
	);
	const recordsChipValue =
		[filters.waveOnly && "Podejrzane fale", filters.negativeOnly && "Tylko negatywne"]
			.filter(Boolean)
			.join(" · ") || "Wszystkie rekordy";
	const mobileChips = [
		{
			id: "records",
			label: "Rekordy",
			value: recordsChipValue,
			active: !!filters.waveOnly || !!filters.negativeOnly,
			picker: recordToggles,
		},
		{
			id: "window",
			label: "Okres",
			value: WINDOW_OPTIONS.find((o) => o.value === filters.window)?.label ?? "Cały okres",
			active: !!filters.window,
			picker: windowPicker,
		},
		{
			id: "ai",
			label: "Werdykt AI",
			value: AI_VERDICT_LABEL[filters.aiVerdict ?? "all"],
			active: !!filters.aiVerdict,
			picker: aiVerdictSelect("h-10 w-full"),
		},
		{
			id: "source",
			label: "Źródło",
			value: SOURCE_LABEL[filters.source ?? "all"],
			active: !!filters.source,
			picker: sourceSelect("h-10 w-full"),
		},
	];

	// Detal grupy: na desktopie prawa kolumna, na telefonie pelnoekranowy arkusz.
	const detailContent = !selected ? (
		<Centered>
			<span className="text-muted-foreground">
				Wybierz grupę z listy, aby zobaczyć rekordy źródłowe.
			</span>
		</Centered>
	) : detail.isLoading ? (
		<Centered>
			<Loader2 className="mr-2 size-4 animate-spin" /> Ładowanie rekordów…
		</Centered>
	) : detail.error || !detail.data ? (
		<Centered>
			<div className="text-center">
				<div>Nie udało się pobrać rekordów.</div>
				<Button variant="outline" size="sm" className="mt-2" onClick={() => detail.refetch()}>
					Spróbuj ponownie
				</Button>
			</div>
		</Centered>
	) : (
		<DetailPanel
			data={detail.data}
			overridePending={override.isPending}
			onDelete={(input) => {
				setToDelete(input);
				setReason("");
			}}
			onOverride={(guardMatch) =>
				override.mutate({
					queryNorm: detail.data!.queryNorm,
					kod07: detail.data!.kod07,
					guardMatch,
				})
			}
		/>
	);

	return (
		<div className="flex h-full flex-col gap-4 p-4 max-md:gap-3 max-md:p-3">
			{isBelowMd ? (
				// Telefon: tytul i opis w pasku gornym, bez powtorzenia pod nim.
				<PageHeader title="Jakość Asystenta" description={PAGE_DESCRIPTION} />
			) : (
				<div className="flex items-center gap-2">
					<Shield className="size-5 text-primary" />
					<h1 className="text-lg font-semibold">Jakość Asystenta</h1>
					<span className="text-sm text-muted-foreground">{PAGE_DESCRIPTION}</span>
				</div>
			)}

			{/* Karty podsumowania */}
			{isBelowMd ? (
				<MobileStatGrid
					stats={[
						{
							label: "Podejrzane fale",
							value: summary?.suspectedWaves ?? 0,
							accent: (summary?.suspectedWaves ?? 0) > 0,
						},
						{
							label: "Produkty ukryte",
							value: summary?.affectedProducts ?? 0,
							accent: (summary?.affectedProducts ?? 0) > 0,
						},
						{ label: "Rekordy 24h", value: summary?.records24h ?? 0 },
						{ label: "Rekordy 7 dni", value: summary?.records7d ?? 0 },
						{ label: "Usunięte dziś", value: summary?.deletedToday ?? 0 },
					]}
				/>
			) : (
				<div className="grid grid-cols-2 gap-2 md:grid-cols-5">
					<SummaryCard label="Podejrzane fale" value={summary?.suspectedWaves ?? 0} accent />
					<SummaryCard label="Produkty ukryte" value={summary?.affectedProducts ?? 0} accent />
					<SummaryCard label="Rekordy 24h" value={summary?.records24h ?? 0} />
					<SummaryCard label="Rekordy 7 dni" value={summary?.records7d ?? 0} />
					<SummaryCard label="Usunięte dziś" value={summary?.deletedToday ?? 0} />
				</div>
			)}

			{/* Filtry. Telefon: wyszukiwarka zostaje na wierzchu (Enter = szukaj),
			    reszta w chipach i arkuszu „Filtry”. */}
			{isBelowMd && (
				<Input
					type="search"
					enterKeyHint="search"
					aria-label="Szukaj"
					value={searchInput}
					onChange={(e) => setSearchInput(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Enter") applySearch();
					}}
					placeholder="Szukaj: fraza / nazwa / BLOZ…"
					className="h-10"
				/>
			)}
			<MobileFilters
				className="rounded-md border"
				chips={mobileChips}
				sheetContent={
					<>
						<MobileFilterField label="Rekordy">{recordToggles}</MobileFilterField>
						<MobileFilterField label="Okres">{windowPicker}</MobileFilterField>
						<MobileFilterField label="Werdykt AI">
							{aiVerdictSelect("h-10 w-full")}
						</MobileFilterField>
						<MobileFilterField label="Źródło">{sourceSelect("h-10 w-full")}</MobileFilterField>
					</>
				}
				onClear={resetFilters}
				resultLabel={
					overview.isFetching
						? "Pokaż wyniki"
						: `Pokaż ${polishCount(total, "grupę", "grupy", "grup")}`
				}
			>
				<div className="flex flex-wrap items-center gap-2">
					<Input
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter") applySearch();
						}}
						placeholder="Szukaj: fraza / nazwa / BLOZ…"
						className="h-8 w-64"
					/>
					<Chip active={!!filters.waveOnly} onClick={() => patch({ waveOnly: !filters.waveOnly })}>
						Podejrzane fale
					</Chip>
					<Chip
						active={!!filters.negativeOnly}
						onClick={() => patch({ negativeOnly: !filters.negativeOnly })}
					>
						Tylko negatywne
					</Chip>
					<Chip active={filters.window === "24h"} onClick={() => toggleWindow("24h")}>
						Ostatnie 24h
					</Chip>
					<Chip active={filters.window === "7d"} onClick={() => toggleWindow("7d")}>
						7 dni
					</Chip>
					{aiVerdictSelect("h-8 w-40")}
					{sourceSelect("h-8 w-36")}
					<Button variant="ghost" size="sm" onClick={resetFilters} className="h-8">
						<RotateCcw className="mr-1 size-3" /> Reset
					</Button>
				</div>
			</MobileFilters>

			{/* Master-detail */}
			<div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
				{/* Kolejka grup */}
				<div className="flex min-h-0 flex-col overflow-hidden rounded-md border border-border">
					<div className="flex items-center justify-between border-b border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
						<span>{total} grup (zapytanie × produkt)</span>
						<span>próg ukrycia: {threshold} aptek</span>
					</div>
					<div className="min-h-0 flex-1 overflow-auto">
						{overview.isLoading ? (
							<Centered>
								<Loader2 className="mr-2 size-4 animate-spin" /> Ładowanie…
							</Centered>
						) : overview.error ? (
							<Centered>
								<div className="text-center">
									<AlertTriangle className="mx-auto mb-2 size-5 text-status-warning-text" />
									<div>Nie udało się pobrać listy.</div>
									<Button
										variant="outline"
										size="sm"
										className="mt-2"
										onClick={() => overview.refetch()}
									>
										Spróbuj ponownie
									</Button>
								</div>
							</Centered>
						) : groups.length === 0 ? (
							<Centered>
								<div className="text-center text-muted-foreground">
									<div>Brak podejrzanych wpisów w wybranym filtrze.</div>
									<Button variant="outline" size="sm" className="mt-2" onClick={resetFilters}>
										Wyczyść filtry
									</Button>
								</div>
							</Centered>
						) : (
							<ul>
								{groups.map((g) => (
									<GroupRow
										key={`${g.queryNorm}|${g.kod07}`}
										g={g}
										active={selected?.query === g.queryNorm && selected?.kod07 === g.kod07}
										onClick={() => setSelected({ query: g.queryNorm, kod07: g.kod07 })}
									/>
								))}
							</ul>
						)}
					</div>
					{total > (filters.pageSize ?? PAGE_SIZE) && (
						<div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs">
							<Button
								variant="ghost"
								size="sm"
								className="max-md:h-9"
								disabled={(filters.page ?? 0) === 0}
								onClick={() => setFilters((f) => ({ ...f, page: Math.max(0, (f.page ?? 0) - 1) }))}
							>
								← Poprzednia
							</Button>
							<span className="text-muted-foreground">strona {(filters.page ?? 0) + 1}</span>
							<Button
								variant="ghost"
								size="sm"
								className="max-md:h-9"
								disabled={((filters.page ?? 0) + 1) * (filters.pageSize ?? PAGE_SIZE) >= total}
								onClick={() => setFilters((f) => ({ ...f, page: (f.page ?? 0) + 1 }))}
							>
								Następna →
							</Button>
						</div>
					)}
				</div>

				{/* Detal */}
				{isBelowMd ? (
					<BottomSheet
						full
						open={!!selected}
						onOpenChange={(open) => {
							if (!open) setSelected(null);
						}}
						title="Rekordy źródłowe"
						data-testid="asystent-jakosc-detail-sheet"
						bodyClassName="px-0 pb-0"
					>
						{detailContent}
					</BottomSheet>
				) : (
					<div className="flex min-h-0 flex-col overflow-hidden rounded-md border border-border">
						{detailContent}
					</div>
				)}
			</div>

			{/* Potwierdzenie usuniecia */}
			<ConfirmDialog
				open={!!toDelete}
				onOpenChange={(o) => {
					if (!o) setToDelete(null);
				}}
				variant="destructive"
				title="Usunąć rekord z bazy?"
				description="Rekord zostanie trwale usunięty ze źródła. Zapiszemy snapshot w audycie; ponowna ocena AI wyzwoli się przy następnym wyszukiwaniu."
				confirmLabel="Usuń rekord z bazy"
				isLoading={del.isPending}
				onConfirm={() => {
					if (!toDelete || !reason.trim()) return;
					del.mutate({ ...toDelete, reason: reason.trim() });
				}}
			>
				{toDelete && (
					<div className="space-y-2">
						<div className="rounded-md bg-muted/50 p-2 text-xs">
							<Field
								k="Źródło"
								v={toDelete.source === "feedback" ? "Feedback (głos apteki)" : "Werdykt AI"}
							/>
							<Field k="Fraza" v={toDelete.queryNorm} />
							<Field k="Produkt (BLOZ)" v={toDelete.kod07} />
							{toDelete.idfirm != null && <Field k="Apteka (idfirm)" v={String(toDelete.idfirm)} />}
							{toDelete.updatedAt && <Field k="Znacznik" v={fmtDate(toDelete.updatedAt)} />}
						</div>
						<div>
							<Label htmlFor="del-reason" className="text-xs">
								Powód (wymagany)
							</Label>
							<Input
								id="del-reason"
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								placeholder="np. błędna ocena — produkt pasuje do frazy"
								className="mt-1 h-8 max-md:h-10"
							/>
							{!reason.trim() && (
								<p className="mt-1 text-xs text-muted-foreground">
									Podaj powód, aby aktywować usunięcie.
								</p>
							)}
						</div>
					</div>
				)}
			</ConfirmDialog>
		</div>
	);
}

function Centered({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex h-full min-h-40 items-center justify-center p-4 text-sm">{children}</div>
	);
}

function Field({ k, v }: { k: string; v: string }) {
	return (
		<div className="flex justify-between gap-3">
			<span className="text-muted-foreground">{k}</span>
			<span className="truncate font-medium">{v}</span>
		</div>
	);
}

function GroupRow({
	g,
	active,
	onClick,
}: {
	g: OverviewGroup;
	active: boolean;
	onClick: () => void;
}) {
	return (
		<li>
			<button
				type="button"
				onClick={onClick}
				className={`flex w-full flex-col gap-1 border-b border-border px-3 py-2 text-left transition-colors ${
					active ? "bg-accent" : "hover:bg-muted/50"
				}`}
			>
				<div className="flex items-center gap-2">
					<Badge className={`text-[10px] ${RISK_CLASS[g.risk]}`}>{RISK_LABEL[g.risk]}</Badge>
					<span className="truncate text-sm font-medium">{g.productName ?? g.kod07}</span>
					{g.hidden && (
						<Badge variant="destructive" className="text-[10px]">
							ukryty
						</Badge>
					)}
				</div>
				<div className="truncate text-xs text-muted-foreground">
					„{g.queryNorm}" · BLOZ {g.kod07}
				</div>
				<div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
					<span className="inline-flex items-center gap-0.5 text-negative">
						<ThumbsDown className="size-3" /> {g.downCount}
					</span>
					<span className="inline-flex items-center gap-0.5">
						<ThumbsUp className="size-3" /> {g.upCount}
					</span>
					{g.downLast24h >= 3 && (
						<span className="text-status-warning-text">fala: {g.downLast24h}/24h</span>
					)}
					{g.hasVerdict && (
						<span>AI: {g.guardMatch === false ? "odrzuca" : g.guardMatch ? "pasuje" : "—"}</span>
					)}
					<span className="ml-auto">{fmtDate(g.lastActivity)}</span>
				</div>
			</button>
		</li>
	);
}

function DetailPanel({
	data,
	overridePending,
	onDelete,
	onOverride,
}: {
	data: DetailResponse;
	overridePending: boolean;
	onDelete: (input: DeleteRecordInput) => void;
	onOverride: (guardMatch: boolean) => void;
}) {
	const match = data.verdict?.guardMatch ?? null;
	return (
		<div className="flex min-h-0 flex-col">
			<div className="border-b border-border bg-muted/40 px-3 py-2">
				<div className="text-sm font-medium">{data.productName ?? data.kod07}</div>
				<div className="text-xs text-muted-foreground max-md:break-words">
					„{data.queryNorm}" · BLOZ {data.kod07}
					{data.hidden && " · produkt ukryty w wynikach"}
				</div>
			</div>
			<div className="min-h-0 flex-1 overflow-auto p-3">
				{/* Opis AI (wskazania) — pełny, to na jego podstawie oceniamy dopasowanie */}
				<h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
					Opis AI (wskazania)
				</h3>
				{data.aiDescription ? (
					<div className="mb-4">
						{/* Ten sam renderer co panel Asystenta — sekcje Opis/Wskazania/Dawkowanie/… */}
						<AiDescriptionRenderer
							text={data.aiDescription}
							className="space-y-1 text-sm leading-snug"
						/>
						<p className="mt-1.5 text-xs text-muted-foreground">
							źródło: ulotka URPL + dane producenta
							{data.enrichmentGeneratedAt &&
								` · wygenerowano ${fmtDate(data.enrichmentGeneratedAt)}`}
						</p>
					</div>
				) : (
					<p className="mb-4 text-sm text-muted-foreground">Brak opisu AI dla tego produktu.</p>
				)}

				{/* Werdykt AI + ręczny override */}
				<h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Werdykt AI</h3>
				<div className="mb-2 flex items-center justify-between rounded-md border border-border p-2 text-sm max-md:gap-2">
					<div>
						<div>
							Dopasowanie:{" "}
							<span className="font-medium">
								{match === false ? "nie pasuje" : match ? "pasuje" : "brak oceny"}
							</span>
							{data.verdict && !data.verdict.fresh && (
								<span className="ml-2 text-xs text-status-warning-text">(nieaktualny)</span>
							)}
						</div>
						<div className="text-xs text-muted-foreground">
							rerank: {data.verdict?.rerankScore?.toFixed(3) ?? "—"}
							{data.verdict && ` · ${fmtDate(data.verdict.guardAt ?? data.verdict.rerankAt)}`}
						</div>
					</div>
					{data.verdict && (
						<Button
							variant="ghost"
							size="sm"
							className="text-destructive max-md:h-9 max-md:shrink-0"
							onClick={() =>
								onDelete({
									source: "verdict",
									queryNorm: data.queryNorm,
									kod07: data.kod07,
									reason: "",
									updatedAt: data.verdict?.guardAt ?? data.verdict?.rerankAt ?? undefined,
								})
							}
						>
							<Trash2 className="mr-1 size-3" /> Usuń
						</Button>
					)}
				</div>
				{/* Ręczna korekta: gdy AI ocenia źle, admin wymusza werdykt (zostaje). */}
				<div className="mb-4 flex items-center gap-2 max-md:flex-wrap">
					<span className="text-xs text-muted-foreground">Ustaw ręcznie:</span>
					<Button
						variant={match === true ? "default" : "outline"}
						size="sm"
						className="max-md:h-9"
						disabled={overridePending || match === true}
						onClick={() => onOverride(true)}
					>
						<Check className="mr-1 size-3" /> Pasuje
					</Button>
					<Button
						variant={match === false ? "destructive" : "outline"}
						size="sm"
						className="max-md:h-9"
						disabled={overridePending || match === false}
						onClick={() => onOverride(false)}
					>
						<X className="mr-1 size-3" /> Nie pasuje
					</Button>
				</div>

				{/* Głosy farmaceutów (łapki) */}
				<h3 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
					Głosy farmaceutów ({data.feedback.length})
				</h3>
				{data.feedback.length === 0 ? (
					<p className="text-sm text-muted-foreground">
						Brak głosów. Pojawią się tu, gdy farmaceuci ocenią ten produkt łapką w Asystencie (łapka
						w dół „nie pasuje" widoczna po filtrze „Tylko negatywne").
					</p>
				) : (
					<ul className="space-y-1">
						{data.feedback.map((f) => (
							<li
								key={f.idfirm}
								className="flex items-center justify-between rounded-md border border-border px-2 py-1 text-sm max-md:gap-2"
							>
								<span className="inline-flex items-center gap-2 max-md:flex-wrap max-md:gap-x-2 max-md:gap-y-0">
									{f.vote === -1 ? (
										<ThumbsDown className="size-3 text-negative" />
									) : (
										<ThumbsUp className="size-3" />
									)}
									apteka {f.idfirm}
									<span className="text-xs text-muted-foreground">{fmtDate(f.createdAt)}</span>
								</span>
								<Button
									variant="ghost"
									size="sm"
									className="text-destructive max-md:h-9 max-md:shrink-0"
									onClick={() =>
										onDelete({
											source: "feedback",
											queryNorm: data.queryNorm,
											kod07: data.kod07,
											idfirm: f.idfirm,
											reason: "",
											updatedAt: f.createdAt ?? undefined,
										})
									}
								>
									<Trash2 className="mr-1 size-3" /> Usuń
								</Button>
							</li>
						))}
					</ul>
				)}
			</div>
		</div>
	);
}
